/**
 * Game Color System
 *
 * Centralized color definitions for game components.
 * These are hex values needed for Canvas API rendering.
 *
 * Colors are organized semantically and aligned with the design system.
 * When changing brand colors, update these values to maintain consistency.
 *
 * Based on design system in globals.css:
 * - brand-blue: oklch(0.623 0.214 259.815) ≈ #3B82F6
 * - brand-purple: oklch(0.714 0.203 305.504) ≈ #8B5CF6
 * - brand-pink: oklch(0.718 0.202 349.761) ≈ #EC4899
 * - success: oklch(0.527 0.154 150.069) ≈ #10B981
 * - warning: oklch(0.769 0.188 70.08) ≈ #F59E0B
 * - destructive: oklch(0.577 0.245 27.325) ≈ #EF4444
 */

// ============================================================================
// Core Brand Colors (hex equivalents for Canvas)
// ============================================================================

export const GAME_COLORS = {
  // Primary brand colors
  blue: '#3B82F6',
  purple: '#8B5CF6',
  pink: '#EC4899',

  // Semantic colors
  success: '#10B981',
  warning: '#F59E0B',
  destructive: '#EF4444',

  // Extended palette for games (derived from brand)
  cyan: '#06B6D4',
  lime: '#84CC16',
  orange: '#F97316',
  indigo: '#6366F1',

  // Neutrals
  white: '#FFFFFF',
  grayLight: '#d1d5db',
  grayDark: '#1f2937',
} as const;

// ============================================================================
// Game-Specific Color Palettes
// ============================================================================

/**
 * Color palettes for tile-based games (Demo, Memory, etc.)
 * Each difficulty level uses progressively more colors.
 */
const TILE_GAME_PALETTES = {
  /** 4 colors - for easy/beginner levels */
  easy: [
    GAME_COLORS.blue,
    GAME_COLORS.destructive,
    GAME_COLORS.success,
    GAME_COLORS.warning,
  ],

  /** 6 colors - for medium difficulty */
  medium: [
    GAME_COLORS.blue,
    GAME_COLORS.destructive,
    GAME_COLORS.success,
    GAME_COLORS.warning,
    GAME_COLORS.purple,
    GAME_COLORS.pink,
  ],

  /** 8 colors - for hard difficulty */
  hard: [
    GAME_COLORS.blue,
    GAME_COLORS.destructive,
    GAME_COLORS.success,
    GAME_COLORS.warning,
    GAME_COLORS.purple,
    GAME_COLORS.pink,
    GAME_COLORS.cyan,
    GAME_COLORS.lime,
  ],

  /** 10 colors - for expert difficulty */
  expert: [
    GAME_COLORS.blue,
    GAME_COLORS.destructive,
    GAME_COLORS.success,
    GAME_COLORS.warning,
    GAME_COLORS.purple,
    GAME_COLORS.pink,
    GAME_COLORS.cyan,
    GAME_COLORS.lime,
    GAME_COLORS.orange,
    GAME_COLORS.indigo,
  ],
} as const;

// ============================================================================
// Maze Game Colors
// ============================================================================

export const MAZE_COLORS = {
  /** Entrance zone gradient start (semi-transparent) */
  entranceGradientStart: 'rgba(59, 130, 246, 0.3)', // blue-500 @ 30%
  entranceGradientEnd: 'rgba(255, 255, 255, 0)',

  /** Exit zone gradient start (semi-transparent) */
  exitGradientStart: 'rgba(139, 92, 246, 0.3)', // purple-500 @ 30%
  exitGradientEnd: 'rgba(255, 255, 255, 0)',

  /** Voronoi (photo / shape) mazes: the entry and exit CELLS are tinted so the
   *  marker reads as "this cell" even when the cell is too small for a glyph. */
  entryTint: 'rgba(59, 130, 246, 0.28)', // blue-500 — drawn UNDER the walls
  exitTint: 'rgba(245, 158, 11, 0.32)', // amber-500
  /** The entry / exit room's OWN walls are re-stroked in these (openings stay open). */
  entryRing: '#2563eb', // blue-600
  exitRing: '#d97706', // amber-600
  /** Dark theme: brighter hues + stronger tints — blue-600 on #1a1922 was invisible. */
  entryTintDark: 'rgba(96, 165, 250, 0.42)', // blue-400
  exitTintDark: 'rgba(251, 191, 36, 0.45)', // amber-400
  entryRingDark: '#60a5fa', // blue-400
  exitRingDark: '#fbbf24', // amber-400
  /** Badge disc fill: a surface tone, NOT white — a yellow cheese on white is invisible. */
  badgeFillLight: '#e9eaf2',
  badgeFillDark: '#2b2a37',

  /** Wall colors.
   * Dark wall mirrors the design-system dark --foreground
   * (oklch(0.95 0.01 280)) so walls read as bright light lines. */
  wallLight: GAME_COLORS.grayDark,
  wallDark: '#e8e8ee',

  /** Background colors.
   * Dark background mirrors the design-system dark --background
   * (oklch(0.16 0.015 280)) — a deep cool slate, not pure black. */
  backgroundLight: GAME_COLORS.white,
  backgroundDark: '#1a1922',

  /** Emoji backing "halo": a filled disc drawn behind the start/end
   * emoji so (a) walls don't show through and (b) the colourful emoji
   * has contrast. Light in both themes so the emoji pops on the dark
   * background instead of blending into it. */
  emojiHaloLight: 'rgba(255, 255, 255, 0.92)',
  emojiHaloDark: 'rgba(236, 238, 245, 0.94)',

  /** Shadow */
  shadow: 'rgba(0, 0, 0, 0.2)',
} as const;

// ============================================================================
// Helper: Get CSS variable value at runtime (client-side only)
// ============================================================================

/**
 * Gets computed CSS variable value from the document.
 * Useful for dynamic theming where hex values need to match CSS vars.
 *
 * @example
 * const brandBlue = getCssVariableValue('--brand-blue');
 */
function getCssVariableValue(variableName: string): string {
  if (typeof window === 'undefined') return '';
  return getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
}

/**
 * The canvas background fill. Reads the design-system `--background` token at
 * runtime so the canvas is EXACTLY the same colour as the surrounding form/page
 * (no visible canvas outline). Falls back to the static hex mirrors during SSR
 * or if the token is unreadable. `isDarkMode` only selects the fallback — the
 * live token already reflects the active theme.
 */
export function getMazeBackground(isDarkMode?: boolean): string {
  const token = getCssVariableValue('--background');
  if (token) return token;
  return isDarkMode ? MAZE_COLORS.backgroundDark : MAZE_COLORS.backgroundLight;
}

/** Entry / exit zone colours for the active theme (Voronoi photo / shape mazes). */
export function getZoneColors(isDarkMode: boolean): { entryTint: string; exitTint: string; entryRing: string; exitRing: string } {
  return isDarkMode
    ? { entryTint: MAZE_COLORS.entryTintDark, exitTint: MAZE_COLORS.exitTintDark, entryRing: MAZE_COLORS.entryRingDark, exitRing: MAZE_COLORS.exitRingDark }
    : { entryTint: MAZE_COLORS.entryTint, exitTint: MAZE_COLORS.exitTint, entryRing: MAZE_COLORS.entryRing, exitRing: MAZE_COLORS.exitRing };
}
